import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { RiskRegisterResponseRow } from '@risksmart-app/trpc/src/types';
import type {
  GetRisksFlatQuery,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

function mapTrpcRiskToGraphQL(
  risk: RiskRegisterResponseRow
): GetRisksFlatQuery['risk'][number] {
  return {
    ...risk,
    // impactRatingsForTrend is the same data as impactRatings but used for trend calculation
    // The ordering is already handled in the query
    impactRatingsForTrend: risk.impactRatings,
    controls_aggregate: {
      aggregate: {
        count: risk.controls.length,
      },
    },
    indicators_aggregate: {
      aggregate: {
        count: risk.indicators.length,
      },
    },
    actions_aggregate: {
      aggregate: {
        count: risk.actions.length,
      },
    },
  };
}

/**
 * Maps TRPC risk data to match the GraphQL query structure
 */
export function mapTrpcRisksToGraphQL(
  trpcData: RiskRegisterResponseRow[]
): GetRisksFlatQuery {
  return {
    risk: trpcData.map((risk) => mapTrpcRiskToGraphQL(risk)),
  };
}

type InternalArgs = {
  enterpriseRiskId?: string;
  entityIds?: string[];
};

const useRisksFlatGraphqlVariables = (args: InternalArgs) => {
  const where = useEntityWhereFilter<Risk_Bool_Exp>(Parent_Type_Enum.Risk);

  const conditions: Risk_Bool_Exp = { ...where };
  if (args.enterpriseRiskId) {
    conditions.enterpriseRiskInstance = {
      ...conditions.enterpriseRiskInstance,
      EnterpriseRiskId: { _eq: args.enterpriseRiskId },
    };
  }

  return { where: conditions };
};

const useGetRisksFlatBase = createQueryHook<
  InternalArgs,
  RiskRegisterResponseRow[],
  GetRisksFlatQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.risk.register.queryOptions({
      enterpriseRiskId: args.enterpriseRiskId,
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: mapTrpcRisksToGraphQL,
  graphqlDocument: GetRisksFlatDocument,
  graphqlVariables: useRisksFlatGraphqlVariables,
});

export type UseGetRisksFlatArgs = {
  enterpriseRiskId?: string;
};

/**
 * Fetches risks with automatic entity filtering for both GraphQL and tRPC paths.
 * Entity filtering uses useEntityFilter context internally — consumers don't need to pass entityIds.
 */
export const useGetRisksFlat = (params: {
  queryArgs: UseGetRisksFlatArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetRisksFlatBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
